import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import { FilterParams } from '@claranet/node-search-filter';
import {
    Company,
    configuration as companyConfiguration,
    embed as companyEmbed,
    EmbedCompany,
} from '@data/models/company.model';
import { Embedded } from '@data/models/embed.model';
import { ListMetadataKeys } from '@data/models/metadata.model';
import { Tag } from '@data/models/tag.model';
import { toParams } from '@shared/utils/query-to-params';
import * as _ from 'lodash';
import { uniq } from 'lodash';
import { Observable } from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';
import { MetadataService } from './metadata.service';

export const COMPANY_SERVICE_API_ROOT = '/core/v1/companies';

@Injectable()
export class CompanyService {

    private errors;
    private _lastFilter: FilterParams;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private metadataService: MetadataService,
    ) {
        this.errors = this.errorService.companyServiceErrors;
    }

    public getLastFilter(): FilterParams {
        return this._lastFilter;
    }

    public setLastFilter(lastFilter: FilterParams) {
        this._lastFilter = lastFilter;
    }

    /**
     * Get filtered and sorted company list
     *
     * @returns {Observable<Company[]>} - The company list
     *
     * @memberof CompanyService
     */
    public findCompanies<T extends companyEmbed>(filterParams?: FilterParams, embedFields?: T[]): Observable<EmbedCompany<T>[]> {
        const embed = uniq(embedFields?.map((it) => companyConfiguration[it].embed)).join(',');
        const params = toParams({ ...filterParams, embed });
        return this.http.get<{ companies: Company[]; embedded: Embedded }>(COMPANY_SERVICE_API_ROOT, { params })
            .pipe(
                map(({ companies, embedded }) => companies.map(company => Company.toCompanyEmbedded(company, embedded, embedFields))),
                catchError(this.errorService.errorHandler<EmbedCompany<T>[]>(this.errors, 'Récupération des sociétés', []))
            );
    }

    public fetchCompaniesByName(filterParams?: FilterParams): Observable<Company[]> {
        const params = toParams(filterParams);
        return this.http.get<{ companies: Company[] }>(COMPANY_SERVICE_API_ROOT, { params }).pipe(
            map((result) => result.companies),
            catchError(this.errorService.errorHandler<Company[]>(this.errors, 'Récupération des sociétés par nom', []))
        );
    }

    /**
     * Get company by ID
     *
     * @param {string} companyId - The company ID
     * @returns {Observable<Company>} - The company matching the ID
     *
     * @memberof CompanyService
     */
    public getCompany<T extends companyEmbed>(
        companyId: string,
        { includeRemoved = false, embedFields = [] }: { includeRemoved?: boolean; embedFields?: T[] } = {}
    ): Observable<EmbedCompany<T>> {
        const embeds = _.uniq(embedFields.map((it) => companyConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });
        return this.http.get<Company & { embedded: Embedded }>(
            COMPANY_SERVICE_API_ROOT + '/' + companyId,
            { params: params.set('showDeleted', `${includeRemoved}`) },
        ).pipe(
            map(({ embedded, ...company }) => Company.toCompanyEmbedded(company, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedCompany<T>>(this.errors, 'Récupération de la société', null))
        );
    }

    /**
     * Create new company
     *
     * @param {Company} company - The company to create
     * @returns {Observable<Company>} - The created company
     *
     * @memberof CompanyService
     */
    public createCompany(company: Company): Observable<Company> {
        return this.http.post<Company>(COMPANY_SERVICE_API_ROOT, company).pipe(
            map(_company => new Company(_company)),
            catchError(this.errorService.errorHandler<Company>(this.errors, 'Création de la société', null))
        );
    }

    /**
     * Update a company
     *
     * @param {Company} company - The company to update
     * @returns {Observable<Company>} - The updated company
     *
     * @memberof CompanyService
     */
    public updateCompany(company: Company): Observable<Company> {
        return this.http.patch<Company>(`${COMPANY_SERVICE_API_ROOT}/${company.id}`, company).pipe(
            map(_company => new Company(_company)),
            catchError(this.errorService.errorHandler<Company>(this.errors, 'Modification de la société', null))
        );
    }

    public updateCompanyMetadata(companyId: string, metadata: Map<string, string>): Observable<Company> {
        const parsedMetadata = {};
        metadata.forEach((value: string, key: string) => {
            parsedMetadata[key] = value.trim();
        });

        return this.http.post<Company>(`${COMPANY_SERVICE_API_ROOT}/${companyId}:setMetadata`, parsedMetadata)
            .pipe(
                map(_company => new Company(_company)),
                catchError(this.errorService.errorHandler<Company>(this.errors, 'Modification des metadatas de la société', null))
            );
    }

    public fetchTags(filterParams: FilterParams): Observable<Tag[]> {
        const params = toParams(filterParams);
        return this.http.get<{ tags: Tag[] }>(COMPANY_SERVICE_API_ROOT + '/tags', { params }).pipe(
            map((result) => result.tags),
            catchError(this.errorService.errorHandler<Tag[]>(this.errors, 'Récupération des données de tag', []))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(COMPANY_SERVICE_API_ROOT, filterParams, this.errors);
    }

    public deleteCompany(companyId: string): Observable<Company> {
        return this.http.delete<Company>(COMPANY_SERVICE_API_ROOT + '/' + companyId).pipe(
            map(company => new Company(company)),
            catchError(this.errorService.errorHandler<Company>(this.errors, 'Suppression de la société', null))
        );
    }
}
